import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, Typography, CircularProgress, Stack, Chip, Divider } from "@mui/material";
import dayjs from "dayjs";
import { fetchNextAvailability, NextAvailabilityResult } from "../../services/doctors.service";
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { CheckoutModal } from "../Checkout/CheckoutModal";
import { getMyNextAppointment } from "../../services/my-appointments.service";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "../../stripe";

export function AppointmentCTA({
  onAppointmentConfirmed,
}: {
  onAppointmentConfirmed?: () => Promise<void> | void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<NextAvailabilityResult | null>(null);
  const [openCheckout, setOpenCheckout] = useState(false);
  const { user } = useSelector((state: RootState) => state.userInfo);
  
  console.log(data)

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleReject = async () => {
    try {
      setLoading(true);
      const prev = data;
      const res = await fetchNextAvailability({
        after: prev?.slotEnd || undefined,
        doctorId: prev?.doctorId || undefined,
      });
      if (res && prev && res.slotStart === prev.slotStart && res.doctorId === prev.doctorId) {
        toast.info('Não há outro horário mais cedo disponível agora.');
      }
      setData(res);
    } catch {
      toast.error('Falha ao buscar próximo horário.');
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!open) return;
      setLoading(true);
      setData(null);
      try {
        const res = await fetchNextAvailability();
        if (!ignore) setData(res);
      } catch (e: any) {
        if (!ignore) {
          toast.error("Não foi possível carregar os horários.");
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true };
  }, [open]);

  const formatDateTime = (iso?: string | null) => {
    if (!iso) return "-";
    return dayjs(iso).format("DD/MM/YYYY HH:mm");
  };



  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 900,
        mx: "auto",
        textAlign: "center",
      }}
    >
      <Typography variant="h5" fontWeight={600} gutterBottom color="#505152ff">
        Agende sua consulta com um médico credenciado 
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={2}>
       Encontre já o proximo horário disponível
      </Typography>

      <Button variant="outlined" color="primary" onClick={handleOpen}>
        Agendar conslta
      </Button>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>Agendamento</DialogTitle>
        <DialogContent>
          {loading && (
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 3 }}>
              <CircularProgress size={28} />
              <Typography>Buscando horários disponíveis...</Typography>
            </Stack>
          )}

          {!loading && data === null && (
            <Box sx={{ py: 2 }}>
              <Typography align="center" color="text.secondary">
                Nenhum horário disponível no momento.
              </Typography>
            </Box>
          )}

          {!loading && data && (
            <Box sx={{ py: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Próximo horário disponível
              </Typography>

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="body2" color="text.secondary">Médico:</Typography>
                  <Chip label={data.doctorName || data.doctorId} size="small" />
                </Stack>

                {data.local && (
                  <Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" color="text.secondary">Local:</Typography>
                      <Chip label={data.local.localName || data.local.id} size="small" />
                    </Stack>
                    {(data.local.address || data.local.city) && (
                      <Typography variant="caption" color="text.secondary">
                        {data.local.address || ""} {data.local.city ? `- ${data.local.city}` : ""}
                      </Typography>
                    )}
                  </Stack>
                )}

                <Divider sx={{ my: 1 }} />

                <Stack direction="row" spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="caption" color="text.secondary">Data</Typography>
                    <Typography fontWeight={500}>{formatDateTime(data.slotStart)}</Typography>
                  </Box>
                 
                </Stack>
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'center', sm: 'space-between' } }}>
          {data && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
              <Button 
                onClick={() => setOpenCheckout(true)} 
                variant="outlined" 
                color="info"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <CheckOutlinedIcon /> Aceitar
              </Button>
              <Button 
                variant="outlined" 
                color="error"
                onClick={handleReject}
                disabled={loading}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                <ClearOutlinedIcon /> Negar
              </Button>
            </Stack>
          )}
          <Button
            onClick={handleClose}
            color="inherit"
            sx={{ ml: { sm: 'auto' }, width: { xs: '100%', sm: 'auto' } }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>
      
      <Elements stripe={stripePromise}>
        <CheckoutModal
          open={openCheckout}
          onClose={() => setOpenCheckout(false)}
          onPaid={async () => {
            // Fecha modais
            setOpenCheckout(false);
            setOpen(false);

            // Aguarda o webhook criar/confirmar o appointment e então atualiza o card "my-next" na Home.
            // (em dev, pode demorar alguns segundos dependendo do Stripe CLI/webhook)
            const expectedScheduleId = data?.scheduleId ?? null;
            const expectedStart = data?.slotStart ?? null;

            const timeoutMs = 15_000;
            const intervalMs = 1_500;
            const startedAt = Date.now();

            while (Date.now() - startedAt < timeoutMs) {
              try {
                const next = await getMyNextAppointment();
                if (
                  next &&
                  (!expectedScheduleId || next.scheduleId === expectedScheduleId) &&
                  (!expectedStart || next.startTime === expectedStart) &&
                  next.status === 'CONFIRMADO'
                ) {
                  break;
                }
              } catch {
                // ignore
              }

              await new Promise((r) => setTimeout(r, intervalMs));
            }

            if (onAppointmentConfirmed) {
              await onAppointmentConfirmed();
            }
          }}
          data={data}
          price={79.9}
        />
      </Elements>
    </Box>
  );
}
